import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@trevor/ui-components';
import style from '../css/module/pivotColumnsRowsSelection.css';
import PivotTableHeading from '../util/PivotTableHeading';

const PIVOT_COLUMNS_ID = 'columns';
const PIVOT_ROWS_ID = 'rows';

const PivotColumnsRowsSelection = ({
  customHeadings,
  dataTable,
  onColumnsRowsUpdate,
}) => {
  if (!dataTable) {
    return null;
  }

  useEffect(() => {
    if (dataTable) {
      setData({
        [PIVOT_COLUMNS_ID]: customHeadings
          ? calculateCustomColumns()
          : calculateDefaultColumns(),
        [PIVOT_ROWS_ID]: customHeadings
          ? calculateCustomRows()
          : calculateDefaultRows(),
      });
    }
  }, [dataTable?.headings()]);

  const calculateDefaultColumns = () => {
    const is4D = dataTable.headings().length === 4;
    const is5D = dataTable.headings().length === 5;
    return is4D
      ? [PivotTableHeading.create(dataTable.headings()[2])]
      : is5D
      ? [PivotTableHeading.create(dataTable.headings()[3])]
      : [PivotTableHeading.create(dataTable.headings()[1])];
  };

  const calculateDefaultRows = () => {
    const is4D = dataTable.headings().length === 4;
    const is5D = dataTable.headings().length === 5;
    return is4D
      ? [
          PivotTableHeading.create(dataTable.headings()[0]),
          PivotTableHeading.create(dataTable.headings()[1]),
        ]
      : is5D
      ? [
          PivotTableHeading.create(dataTable.headings()[0]),
          PivotTableHeading.create(dataTable.headings()[1]),
          PivotTableHeading.create(dataTable.headings()[2]),
        ]
      : [PivotTableHeading.create(dataTable.headings()[0])];
  };

  const calculateCustomColumns = () => {
    const customColumns = customHeadings[PIVOT_COLUMNS_ID].map((e) =>
      PivotTableHeading.fromServer(e),
    );
    const defaultHeadings = dataTable
      .headings()
      .slice(0, dataTable.headings().length - 1);
    // Remove rows that were previously in the query and are not in the headings now
    return customColumns.filter(
      (e) => defaultHeadings.findIndex((e1) => sameHeading(e.heading, e1)) >= 0,
    );
  };

  const sameHeading = (e, e1) =>
    e.columnName().alias() === e1.columnName().alias() &&
    e.columnName().name() === e1.columnName().name();

  const calculateCustomRows = () => {
    const customColumns = calculateCustomColumns();
    const defaultHeadings = dataTable
      .headings()
      .slice(0, dataTable.headings().length - 1)
      .map(PivotTableHeading.create);
    const customRows = customHeadings[PIVOT_ROWS_ID].map(
      PivotTableHeading.fromServer,
    );
    const customData = [...customColumns, ...customRows];
    // Add new headings that were not previously in the query to as a new row
    const newRows = [
      ...customRows,
      ...defaultHeadings.filter((e) => {
        return (
          customData.findIndex((e1) => sameHeading(e.heading, e1.heading)) ===
          -1
        );
      }),
    ];
    // Remove rows that were previously in the query and are not in the headings now
    return newRows.filter((e) => {
      return (
        defaultHeadings.findIndex((e1) => sameHeading(e.heading, e1.heading)) >=
        0
      );
    });
  };

  const [data, setData] = useState({
    [PIVOT_COLUMNS_ID]: customHeadings
      ? calculateCustomColumns()
      : calculateDefaultColumns(),
    [PIVOT_ROWS_ID]: customHeadings
      ? calculateCustomRows()
      : calculateDefaultRows(),
  });

  const getItemStyle = (isDragging, draggableStyle) => ({
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    // dropped outside the list
    if (destination) {
      if (source.droppableId === destination.droppableId) {
        const items = reorder(
          data[source.droppableId],
          source.index,
          destination.index,
        );

        const columns =
          source.droppableId === PIVOT_COLUMNS_ID ? items : [...data.columns];
        const rows =
          source.droppableId === PIVOT_ROWS_ID ? items : [...data.rows];

        setData({
          ...data,
          [PIVOT_COLUMNS_ID]: columns,
          [PIVOT_ROWS_ID]: rows,
        });

        onColumnsRowsUpdate([...columns], [...rows]);
      } else {
        const _result = move(
          data[source.droppableId],
          data[destination.droppableId],
          source,
          destination,
        );

        setData({
          ...data,
          [PIVOT_COLUMNS_ID]: _result.columns,
          [PIVOT_ROWS_ID]: _result.rows,
        });

        onColumnsRowsUpdate([..._result.columns], [..._result.rows]);
      }
    }
  };

  const changeItemSortOption = (sort, index, target) => {
    // asc == 1, desc == -1
    // new sort is opposite of current sort
    const newSort = sort === undefined || sort === 0 ? -1 : sort * -1;

    const newData = [...data[target]];
    newData[index] = PivotTableHeading.create(
      newData[index].heading,
      parseInt(newSort, 10),
    );
    setData({
      ...data,
      [target]: newData,
    });

    if (target === PIVOT_COLUMNS_ID) {
      onColumnsRowsUpdate([...newData], [...data[PIVOT_ROWS_ID]]);
    } else {
      onColumnsRowsUpdate([...data[PIVOT_COLUMNS_ID]], [...newData]);
    }
  };

  const getDefaultSortCriteria = (item) => {
    const is3D = dataTable.headings().length === 3;
    const is4D = dataTable.headings().length === 4;
    const itemIndex = dataTable
      .headings()
      .findIndex((e1) => sameHeading(item.heading, e1));

    // This sort options come from the way columns are sorted by default in the SQL query
    const sorts = is3D
      ? [-1, 0, -1]
      : is4D
      ? [-1, 1, 0, -1]
      : [-1, 1, 1, 0, -1];

    return sorts[itemIndex];
  };

  const getSortCriteria = (item) => item.sort ?? getDefaultSortCriteria(item);

  const buildDraggable = (item, index, target) => (
    <Draggable
      key={item.heading.columnName().name()}
      draggableId={item.heading.columnName().name()}
      index={index}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style,
          )}
          className={style.draggableColumn}
        >
          <div className={`w-100 ${style.draggableInner}`}>
            <span className={style.draggableIcon}>
              <FontAwesomeIcon icon="ellipsis-v" />
              <FontAwesomeIcon icon="ellipsis-v" />
            </span>
            <div className={style.draggableTitle}>
              <div className="ellipsis bold" style={{ width: 'auto' }}>
                {item.heading.columnName().name()}
              </div>
            </div>
            <span key={Math.random()}>
              <span
                data-tooltip-id={`${item}_${item.heading.columnName().name()}`}
                onClick={() =>
                  changeItemSortOption(getSortCriteria(item), index, target)
                }
                className={style.sortIcon}
              >
                <FontAwesomeIcon
                  icon={
                    getSortCriteria(item) === 1
                      ? 'sort-amount-down-alt'
                      : 'sort-amount-down'
                  }
                />
              </span>
              <Tooltip id={`${item}_${item.heading.columnName().name()}`}>
                {getSortCriteria(item) === 1 ? 'sorted asc' : 'sorted desc'}
              </Tooltip>
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );

  const getDropStyle = (snapshot, type) => {
    return {
      height: getDroppableHeight(type, snapshot),
      background: snapshot.isDraggingOver
        ? '#CFE8FC'
        : snapshot.draggingFromThisWith
        ? '#f1f1f1'
        : '#fff',
    };
  };

  const getDroppableHeight = (type, snapshot) => {
    let height = data[type].length * 54;

    if (snapshot.isDraggingOver && !snapshot.draggingFromThisWith) {
      height += 54;
    }

    return `${height}px`;
  };

  return (
    <div className="margin-bottom-10">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="padding-bottom-10 w-100">
          <h5>
            Columns <small>(drag items)</small>
          </h5>
          <Droppable droppableId={PIVOT_COLUMNS_ID}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                className={`${style.ddArea} padding-bottom-10`}
                style={getDropStyle(snapshot, PIVOT_COLUMNS_ID)}
              >
                {data[PIVOT_COLUMNS_ID].map((item, index) =>
                  buildDraggable(item, index, PIVOT_COLUMNS_ID),
                )}
              </div>
            )}
          </Droppable>
        </div>
        <h5>
          Rows <small>(drag items)</small>
        </h5>
        <Droppable droppableId={PIVOT_ROWS_ID}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              className={style.ddArea}
              style={getDropStyle(snapshot, PIVOT_ROWS_ID)}
            >
              {data[PIVOT_ROWS_ID].map((item, index) =>
                buildDraggable(item, index, PIVOT_ROWS_ID),
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default PivotColumnsRowsSelection;
export { PIVOT_COLUMNS_ID, PIVOT_ROWS_ID };
